import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatEUR, formatDateFR, daysBetween, todayISO } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { ArrowUpRight, TrendingUp, Clock, AlertTriangle, Users, Plus } from "lucide-react";

const Kpi = ({ label, value, sub, accent, testId, Icon }) => (
  <div
    data-testid={testId}
    className="rounded-2xl border p-6 bg-white flex flex-col gap-3"
    style={{ borderColor: "#E5E2DC" }}
  >
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider text-[#5C665E] font-medium">
        {label}
      </span>
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: accent + "20", color: accent }}
      >
        <Icon size={16} strokeWidth={2} />
      </div>
    </div>
    <div className="font-mono-num text-3xl font-semibold" style={{ color: accent }}>
      {value}
    </div>
    <div className="text-xs text-[#5C665E]">{sub}</div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [s, f] = await Promise.all([
        api.get("/stats"),
        api.get("/factures"),
      ]);
      setStats(s.data);
      setFactures(f.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const enRetard = factures.filter((f) => f.statut === "en_retard");
  const enAttente = factures.filter((f) => f.statut === "en_attente");
  const recentes = factures.slice(0, 6);

  const togglePaid = async (id) => {
    try {
      await api.patch(`/factures/${id}/toggle-paiement`);
      load();
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) {
    return <div className="text-[#5C665E]">Chargement...</div>;
  }

  return (
    <div data-testid="dashboard-page" className="space-y-10">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">
            Tableau de bord
          </h1>
          <p className="text-[#5C665E] mt-2">
            Vue d&apos;ensemble de vos paiements &middot; {formatDateFR(todayISO())}
          </p>
        </div>
        <button
          data-testid="dashboard-new-invoice"
          onClick={() => navigate("/factures/nouveau")}
          className="rounded-full px-5 py-2.5 text-sm font-semibold text-white btn-lift focus-brand flex items-center gap-2"
          style={{ backgroundColor: "#2C5545" }}
        >
          <Plus size={16} /> Nouvelle facture
        </button>
      </div>

      {/* Alerte En Retard */}
      {enRetard.length > 0 && (
        <div
          data-testid="overdue-alert"
          className="rounded-2xl border p-5 flex items-start gap-4"
          style={{
            backgroundColor: "#FCE8E8",
            borderColor: "#F8B4B4",
          }}
        >
          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={20} color="#B3261E" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-semibold text-[#B3261E]">
              {enRetard.length} facture{enRetard.length > 1 ? "s" : ""} en retard
            </div>
            <div className="text-sm text-[#B3261E] opacity-90 mt-1">
              Montant total en retard :{" "}
              <span className="font-mono-num font-semibold">
                {formatEUR(stats?.total_en_retard)}
              </span>{" "}
              &middot; Pensez à relancer vos clients.
            </div>
          </div>
          <button
            data-testid="overdue-alert-cta"
            onClick={() => navigate("/factures?statut=en_retard")}
            className="text-sm font-semibold text-[#B3261E] hover:underline flex items-center gap-1 flex-shrink-0"
          >
            Voir <ArrowUpRight size={14} />
          </button>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          testId="kpi-encaisse"
          label="Encaissé"
          value={formatEUR(stats?.total_encaisse)}
          sub={`${stats?.count_payee || 0} facture(s) payée(s)`}
          accent="#1E6534"
          Icon={TrendingUp}
        />
        <Kpi
          testId="kpi-attente"
          label="En attente"
          value={formatEUR(stats?.total_en_attente)}
          sub={`${stats?.count_en_attente || 0} facture(s)`}
          accent="#8C6100"
          Icon={Clock}
        />
        <Kpi
          testId="kpi-retard"
          label="En retard"
          value={formatEUR(stats?.total_en_retard)}
          sub={`${stats?.count_en_retard || 0} facture(s)`}
          accent="#B3261E"
          Icon={AlertTriangle}
        />
        <Kpi
          testId="kpi-clients"
          label="Clients"
          value={String(stats?.nb_clients || 0)}
          sub={`${stats?.count_total || 0} facture(s) au total`}
          accent="#2C5545"
          Icon={Users}
        />
      </div>

      {/* Two column: relances + recentes */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Relances */}
        <section
          className="lg:col-span-2 rounded-2xl border bg-white p-6"
          style={{ borderColor: "#E5E2DC" }}
          data-testid="relances-section"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-xl">À relancer</h2>
            <span className="text-xs text-[#5C665E]">
              {enRetard.length + enAttente.length} restantes
            </span>
          </div>
          {enRetard.length === 0 && enAttente.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">🌿</div>
              <p className="text-[#5C665E] text-sm">
                Aucun paiement en attente. Bien joué !
              </p>
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: "#E5E2DC" }}>
              {[...enRetard, ...enAttente].slice(0, 6).map((f) => {
                const days = daysBetween(todayISO(), f.date_echeance);
                return (
                  <li
                    key={f.id}
                    className="py-3 flex items-center justify-between gap-3"
                    data-testid={`relance-item-${f.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">
                        {f.client_nom}
                      </div>
                      <div className="text-xs text-[#5C665E] flex items-center gap-2 mt-0.5">
                        <span className="font-mono-num">{f.numero}</span>
                        <span>&middot;</span>
                        {f.statut === "en_retard" ? (
                          <span className="text-[#B3261E] font-medium">
                            Retard de {Math.abs(days)}j
                          </span>
                        ) : (
                          <span>Dans {days}j</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono-num font-semibold">
                        {formatEUR(f.montant)}
                      </div>
                      <StatusBadge status={f.statut} pulse />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Récentes */}
        <section
          className="lg:col-span-3 rounded-2xl border bg-white p-6"
          style={{ borderColor: "#E5E2DC" }}
          data-testid="recent-section"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-xl">Factures récentes</h2>
            <button
              data-testid="see-all-invoices"
              onClick={() => navigate("/factures")}
              className="text-sm text-[#2C5545] font-semibold flex items-center gap-1 hover:underline"
            >
              Voir tout <ArrowUpRight size={14} />
            </button>
          </div>
          {recentes.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#5C665E] text-sm mb-4">
                Aucune facture pour le moment.
              </p>
              <button
                data-testid="empty-create-invoice"
                onClick={() => navigate("/factures/nouveau")}
                className="rounded-full px-4 py-2 text-sm font-semibold text-white btn-lift"
                style={{ backgroundColor: "#2C5545" }}
              >
                Créer la première
              </button>
            </div>
          ) : (
            <div className="overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-[#5C665E]">
                    <th className="pb-3 font-medium">Facture</th>
                    <th className="pb-3 font-medium">Client</th>
                    <th className="pb-3 font-medium">Échéance</th>
                    <th className="pb-3 font-medium text-right">Montant</th>
                    <th className="pb-3 font-medium text-right">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {recentes.map((f) => (
                    <tr
                      key={f.id}
                      className="row-hover border-t cursor-pointer"
                      style={{ borderColor: "#E5E2DC" }}
                      onClick={() => togglePaid(f.id)}
                      data-testid={`recent-row-${f.id}`}
                    >
                      <td className="py-3 font-mono-num">{f.numero}</td>
                      <td className="py-3 truncate max-w-[140px]">
                        {f.client_nom}
                      </td>
                      <td className="py-3 text-[#5C665E] font-mono-num text-xs">
                        {formatDateFR(f.date_echeance)}
                      </td>
                      <td className="py-3 text-right font-mono-num font-semibold">
                        {formatEUR(f.montant)}
                      </td>
                      <td className="py-3 text-right">
                        <StatusBadge status={f.statut} pulse={f.statut==='en_retard'} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-[#5C665E] mt-3">
                Astuce : cliquez sur une ligne pour basculer le statut de paiement.
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
